import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router';

import {
  type OpportunityStatusOldType,
  type OpportunityType,
  useOpportunityById,
} from '@/api/opportunity';
import { Page, PageFilterProvider } from '@/components/page';
import { PendoPlaceholder } from '@/components/PendoPlaceholder';
import { ReturnToLink } from '@/components/ReturnToLink';
import DetailPageTitle from '@/features/DetailPageTitle';
import ErrorOrChildren from '@/features/ErrorOrChildren';
import { OpportunityTypeSelect } from '@/features/inputs';
import ShareMenu from '@/features/ShareMenu';
import { opportunities } from '@/router/routes/opportunityRoutes';

import OpportunityDetailContent from './OpportunityDetailContent';
import OpportunityDetailKPIMetrics from './OpportunityDetailKPIMetrics';

const getOpportunityStatusLabel = (opportunity?: OpportunityType) => {
  if (!opportunity) {
    return '';
  }

  switch (opportunity.status) {
    case 'CLOSED_WON':
      return t`Closed Won`;
    case 'CLOSED_LOST':
      return t`Closed Lost`;
    case 'PIPELINE':
      return t`Open`;
    default:
      return t`unknown`;
  }
};

const OpportunityDetail = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { opportunity: opportunityId } = useParams();
  const oppType = searchParams.get('oppType') ?? undefined;

  const { opportunity, isLoading, error } = useOpportunityById(opportunityId);

  useEffect(() => {
    if (oppType !== 'pipeline' && oppType !== 'closedWon') {
      setSearchParams({ oppType: 'pipeline' }, { replace: true });
    }
  }, [oppType, setSearchParams]);

  const handleOppTypeChange = (value: OpportunityStatusOldType) => {
    setSearchParams({ oppType: value });
  };

  if (oppType !== 'pipeline' && oppType !== 'closedWon') {
    return null;
  }

  return (
    <Page
      title={t`Opportunities`}
      pageName={getOpportunityStatusLabel(opportunity)}
      exampleAIQuestions={[]}
    >
      <div className="flex flex-col gap-6">
        <PageFilterProvider>
          <ReturnToLink route={opportunities.path} routeName={t`Opportunities`} />
          <div className="flex justify-between">
            <DetailPageTitle
              title={opportunity?.account.company?.name ?? opportunity?.account.name}
              subtitleLabel={<Trans>Opportunity name</Trans>}
              subtitle={opportunity?.name}
              subtitleLink={opportunity?.sfUrl}
              subtitleLinkExternal={true}
              isLoading={isLoading}
            />
            <div className="flex items-start gap-2">
              <PendoPlaceholder />
              <OpportunityTypeSelect
                value={oppType as OpportunityStatusOldType}
                onChange={handleOppTypeChange}
              />
              <ShareMenu notificationPageKey="opportunity" />
            </div>
          </div>
          <ErrorOrChildren
            error={error}
            message404={<Trans>The opportunity you're looking for does not exist</Trans>}
          >
            <OpportunityDetailKPIMetrics opportunityId={opportunityId} />
            {(oppType === 'pipeline' || oppType === 'closedWon') && (
              <OpportunityDetailContent opportunity={opportunity} oppType={oppType} />
            )}
          </ErrorOrChildren>
        </PageFilterProvider>
      </div>
    </Page>
  );
};

export default OpportunityDetail;
